import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Button } from "@design-system/Button/Button";
import { ArrowRightIcon, RevertIcon } from "@icons";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import {
    BatchGroupRow,
    BatchGroupTableMeta,
} from "../views/batchGroupsViewConfig";
import styles from "../../cells/HistoryDetailsCells.module.scss";

export const BatchGroupActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, BatchGroupRow, unknown>) => {
        const meta = table.options.meta as BatchGroupTableMeta | undefined;
        const { isReverted } = row.original;

        const handleRevert = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                meta?.callbacks?.onRevert?.(row.original);
            },
            [meta, row.original],
        );

        const handleExpand = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                meta?.callbacks?.onExpand?.(row.original);
            },
            [meta, row.original],
        );

        return (
            <ViewTableCell className={styles.expandCell} captureClicks>
                <div className={styles.buttonContainer}>
                    {meta?.callbacks?.onRevert && (
                        <Tooltip content={isReverted ? "Reverted" : undefined}>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={isReverted ? undefined : handleRevert}
                                disabled={isReverted}
                                title={isReverted ? undefined : "Undo"}
                                aria-label={isReverted ? "Reverted" : "Undo"}
                                iconOnly
                                className={
                                    isReverted
                                        ? undefined
                                        : tableStyles.showOnHover
                                }
                            >
                                <RevertIcon />
                            </Button>
                        </Tooltip>
                    )}
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        className={styles.expandButton}
                        onClick={handleExpand}
                        aria-label="View group details"
                    >
                        <ArrowRightIcon />
                    </Button>
                </div>
            </ViewTableCell>
        );
    },
);
BatchGroupActionsCell.displayName = "BatchGroupActionsCell";
